import { NodeResult } from "@custom-types/modelRun"
import { ThresholdFormValues } from "@features/results/Result/ThresholdChartContainer/ThresholdForm"
import {
  getHighestPrecision,
  getStepByPrecision,
  getTimestepMinMaxValues,
} from "@features/results/Result/ThresholdChartContainer/ThresholdForm/helpers"
import { Grid, Slider, TextField, Typography } from "@mui/material"
import { Controller, useFormContext } from "react-hook-form"

type BoundarySliderProps = {
  nodeResults?: NodeResult[]
  timestep: string
  name: "event.boundary" | "given_event.boundary"
  showMinMax?: boolean
}

const BoundarySlider = (props: BoundarySliderProps) => {
  const form = useFormContext<ThresholdFormValues>()
  const { min, max } = getTimestepMinMaxValues(props.timestep, props.nodeResults)

  const timestepResults = props.nodeResults?.find(
    (result) => String(result.timestep) === props.timestep,
  )?.data

  function clamp(num: number, min: number, max: number) {
    return num <= min ? min : num >= max ? max : num
  }

  const highestPrecision = clamp(getHighestPrecision(timestepResults ?? []), 0, 100)
  const step = getStepByPrecision(highestPrecision)

  return (
    <Controller
      name={props.name}
      render={({ field }) => (
        <Grid container direction="column">
          <Grid container item gap={2}>
            <Grid item container xs justifyContent="space-between">
              <Grid item xs={12}>
                <Slider
                  marks={[
                    { value: min, label: "" },
                    { value: max, label: "" },
                  ]}
                  step={step}
                  valueLabelDisplay="auto"
                  {...field}
                  min={min}
                  max={max}
                />
              </Grid>

              {(props.showMinMax ?? true) && (
                <>
                  <Grid item>
                    <Typography
                      onClick={() => form.setValue(props.name, min)}
                      sx={{ cursor: "pointer" }}
                    >
                      {min.toFixed(3)}
                    </Typography>
                  </Grid>

                  <Grid item>
                    <Typography
                      onClick={() => form.setValue(props.name, max)}
                      sx={{ cursor: "pointer" }}
                    >
                      {max.toFixed(3)}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>

            <Grid item xs={4}>
              <TextField
                variant="standard"
                size="small"
                {...field}
                type="number"
                InputProps={{ inputProps: { min, max, step: step.toFixed(highestPrecision) } }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    />
  )
}

export default BoundarySlider
